import 'simplebar-react/dist/simplebar.min.css';
import avatar2 from 'assets/images/users/ccc.jpg';
import SimpleBar from 'simplebar-react';
import MicRecorder from "./MicRecorder";
import ChatMessage from "./ChatMessage";
import { useEffect, useRef, useState } from "react";
import { floatTo16BitPCM } from "units/chatbot/utils/utils";
import { connectWebSocket } from "../services/websocketService";
import { adminChatroomSendMessage, fetchMessages } from "units/chatbot/services/chatService";
import { useSelector } from "react-redux";
import ClientMessage from './ClientMessage';
import feather from 'feather-icons';
import { RefreshCw, Send, MessageSquare, Sparkles } from 'lucide-react';
import { useTranslation } from "react-i18next";

const ChatBox = ({ visible, selectedConversation, chatPartner, partnerId, chatToAdmin, botType, chatroomId, withGPT, accessToken }) => {
  const { t } = useTranslation(["user"]);

  const [isRecording, setIsRecording] = useState(false);
  const [ws, setWs] = useState(null);
  const [buffer, setBuffer] = useState([]);
  const [messages, setMessages] = useState([]);
  const [userMessage, setUserMessage] = useState("");
  const [currentCategory, setCurrentCategory] = useState(botType);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isConversationEnded = selectedConversation?.status === "ended";
  const dataIntervalId = useRef(null);
  const selectedMenu = useSelector(state => state.menu.activeItem);
  const user = useSelector((state) => state.userInfo);
  const userId = user?.userId;
  const scrollRef = useRef(null);
  const MAX_ROWS = 10;
  const LINE_HEIGHT = 24;
  const BASE_HEIGHT = 40;
  const containerRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.getScrollElement();
      scrollElement.scrollTo({
        top: scrollElement.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages]);
  useEffect(() => {
    const updateHeight = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const footerH = document.getElementById("main-footer")?.offsetHeight || 0;
      const available = window.innerHeight - rect.top - footerH - 24;
      containerRef.current.style.height = `${Math.max(available, 400)}px`;
    };

    updateHeight();

    // وقتی هدر/فوتر اصلی تغییر اندازه دادن
    const observer = new ResizeObserver(updateHeight);
    ["main-header", "main-footer"].forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    window.addEventListener("resize", updateHeight);
    window.addEventListener("orientationchange", updateHeight);

    // رفرش بعد از mount کامل (وقتی لِی‌اوت جا افتاد)
    const t = setTimeout(updateHeight, 100);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateHeight);
      window.removeEventListener("orientationchange", updateHeight);
      clearTimeout(t);
    };
  }, []);
  useEffect(() => {
    feather.replace();
  }, []);

  const resetChatBoxState = () => {
    setIsRecording(false);
    setWs(null);
    setBuffer([]);
    setMessages([]);
    setUserMessage("");
    setCurrentCategory(null);
    if (dataIntervalId.current) {
      clearInterval(dataIntervalId.current);
      dataIntervalId.current = null;
    }
  };

  useEffect(() => {
    if (!selectedConversation) resetChatBoxState();
  }, [selectedConversation]);

  const wsSender = () => {
    dataIntervalId.current = setInterval(() => {
      if (!isRecording || !ws || ws.readyState !== WebSocket.OPEN) {
        clearInterval(dataIntervalId.current);
        return;
      }
      const audioData = buffer.splice(0, 640);
      if (audioData.length > 0) {
        ws.send(floatTo16BitPCM(audioData));
      }
    }, 20);
  };

  const handleMicToggle = (isActive) => {
    if (!isActive) {
      clearInterval(dataIntervalId.current);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
      setBuffer([]);
      setWs(null);
    } else {
      const newWebSocket = connectWebSocket(new Date().getTime(), new Date().getTime());
      setWs(newWebSocket);
      newWebSocket.onopen = () => console.log("Reconnected to server successfully.");
      newWebSocket.onerror = (error) => console.error('WebSocket error:', error);
      newWebSocket.onclose = () => console.log('WebSocket connection closed.');
      newWebSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const predictedTxt = data.predicted_txt;
          if (predictedTxt) setUserMessage(predictedTxt);
        } catch (error) {
          console.error("Failed to parse JSON:", error);
        }
      };
    }
    setIsRecording(isActive);
  };

  const handleSendMessage = async () => {
    if (userMessage.trim() === "") return;
    setUserMessage("");
    try {
      const data = await adminChatroomSendMessage({ accessToken, chatroomId, message: userMessage });
      if (data?.ok) {
        const botResponse = {
          message: data?.data?.message || "No response from bot",
          senderId: data?.data?.senderId,
          is_sent_by_user: data?.data?.senderId === partnerId ? true : false
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  const handleMessageChange = (e) => setUserMessage(e.target.value);

  useEffect(() => {
    const websocket = connectWebSocket(new Date().getTime(), new Date().getTime());
    websocket.onopen = () => console.log("Connected to server successfully.");
    websocket.onerror = (error) => console.error('WebSocket error:', error);
    websocket.onclose = () => console.log('WebSocket connection closed.');
    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const predictedTxt = data.predicted_txt;
        if (predictedTxt) setUserMessage(predictedTxt);
      } catch (error) {
        console.error("Failed to parse JSON:", error);
      }
    };
    setWs(websocket);
    return () => {
      if (websocket.readyState === WebSocket.OPEN) websocket.close();
    };
  }, []);

  useEffect(() => {
    if (isRecording && buffer.length > 0) {
      wsSender();
    }
    return () => clearInterval(dataIntervalId.current);
  }, [buffer, isRecording]);

  const loadMessages = async () => {
    if (selectedConversation) {
      try {
        const data = await fetchMessages({ accessToken, chatroomId: selectedConversation?.id });
        if (data?.ok) {
          setMessages(data?.data.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    }
  };

  useEffect(() => { loadMessages(); }, [selectedConversation]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try { await loadMessages(); }
    finally { setTimeout(() => setIsRefreshing(false), 500); }
  };

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col mx-auto my-3 w-full overflow-hidden
                       rounded-3xl
                       bg-white/80 dark:bg-slate-900/70
                       backdrop-blur-2xl
                       ring-1 ring-slate-200/70 dark:ring-white/10
                       shadow-[0_20px_60px_-25px_rgba(13,148,136,0.35)]
                       dark:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.9)]"
      style={{ height: "calc(100vh - 120px)", minHeight: 400 }}
    >
      {/* ambient blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full
                                bg-teal-400/10 dark:bg-teal-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full
                                bg-amber-300/15 dark:bg-amber-400/5 blur-3xl" />
      </div>

      {/* gold top line animated */}
      <div className="absolute top-0 inset-x-0 h-[3px] z-20
                            bg-gradient-to-r from-teal-500 via-amber-400 to-teal-500
                            bg-[length:200%_100%] animate-[shimmer_4s_linear_infinite]" />

      {/* ===================== HEADER ===================== */}
      <div
        id="chat-header"
        className="relative z-10 flex items-center justify-between px-4 sm:px-5 py-3.5
                           border-b border-slate-200/70 dark:border-white/5
                           bg-gradient-to-l from-white/60 to-teal-50/60
                           dark:from-slate-900/60 dark:to-slate-800/40
                           flex-shrink-0"
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Avatar with ring */}
          <div className="relative flex-shrink-0">
            <div className="absolute -inset-0.5 rounded-full
                                        bg-gradient-to-br from-teal-500 via-teal-400 to-amber-400
                                        blur-[3px] opacity-60" />
            <div className="relative w-10 h-10 rounded-full overflow-hidden
                                        ring-2 ring-white dark:ring-slate-900">
              <img src={avatar2} alt="" className="w-full h-full object-cover" />
            </div>
            {/* online dot */}
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full
                                         bg-emerald-500 ring-2 ring-white dark:ring-slate-900" />
          </div>

          <div className="min-w-0">
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
              {chatPartner || t("chatBox.defaultPartnerName")}
            </h3>
          </div>
        </div>

        {/* Refresh button */}
        <button
          onClick={handleRefresh}
          title={t("chatBox.refresh")}
          className="group relative h-9 w-9 rounded-xl flex items-center justify-center
                               bg-white/70 dark:bg-slate-800/70
                               ring-1 ring-slate-200/70 dark:ring-white/10
                               text-teal-700 dark:text-teal-300
                               hover:bg-gradient-to-br hover:from-teal-600 hover:to-teal-700
                               hover:text-white
                               hover:ring-teal-500/40
                               hover:shadow-lg hover:shadow-teal-600/30
                               active:scale-95
                               transition-all duration-300"
        >
          <RefreshCw
            size={16}
            className={`transition-transform duration-700 ${isRefreshing ? "animate-spin" : "group-hover:rotate-180"}`}
          />
        </button>
      </div>

      {/* ===================== MESSAGES ===================== */}
      <div className="relative z-10 flex-1 min-h-0">
        <SimpleBar className="h-full px-3 sm:px-5 py-4" ref={scrollRef}>
          {/* Empty state */}
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-10">
              <div className="w-16 h-16 rounded-2xl
                                            bg-gradient-to-br from-teal-500 to-teal-700
                                            flex items-center justify-center
                                            shadow-lg shadow-teal-700/30
                                            ring-1 ring-white/20">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
                  {t("chatBox.emptyTitle")}
                </p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  {t("chatBox.emptySubtitle")}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {!withGPT
                ? messages?.map((msg, index) => (
                  <ChatMessage
                    key={index}
                    createdAt={msg.createdAt}
                    message={msg.message}
                    isSender={msg?.senderId}
                    userId={userId}
                  />
                ))
                : messages.map((msg, index) => (
                  <ClientMessage
                    createdAt={msg.createdAt}
                    key={index}
                    message={msg.message}
                    response={msg.response.message}
                    isSender={msg?.is_sent_by_user}
                    userId={userId}
                  />
                ))}
            </div>
          )}
        </SimpleBar>

        {/* fade at top of messages */}
        <div className="pointer-events-none absolute top-0 inset-x-0 h-4
                                bg-gradient-to-b from-white/80 to-transparent
                                dark:from-slate-900/80" />
      </div>

      {/* ===================== INPUT ===================== */}
      {!withGPT && (
        <div
          id="chat-user-input"
          className="relative z-10 flex-shrink-0 p-3 sm:p-4
                               border-t border-slate-200/70 dark:border-white/5
                               bg-gradient-to-l from-white/60 to-teal-50/40
                               dark:from-slate-900/60 dark:to-slate-800/30"
        >
          <div className="flex gap-2 items-center">


            {/* Textarea */}
            <div className="relative flex-1">
              <textarea
                value={isConversationEnded ? t("chatBox.conversationEnded") : userMessage}
                disabled={isConversationEnded}
                onChange={(e) => {
                  setUserMessage(e.target.value);
                  const textarea = e.target;
                  if (textarea.value.trim() === "") {
                    textarea.style.height = `${BASE_HEIGHT}px`;
                    textarea.style.overflowY = "hidden";
                    return;
                  }
                  textarea.style.height = "auto";
                  const scrollHeight = textarea.scrollHeight;
                  const maxHeight = MAX_ROWS * LINE_HEIGHT;
                  if (scrollHeight > maxHeight) {
                    textarea.style.height = `${maxHeight}px`;
                    textarea.style.overflowY = "auto";
                  } else {
                    textarea.style.height = `${scrollHeight}px`;
                    textarea.style.overflowY = "hidden";
                  }
                }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                    const textarea = e.target;
                    textarea.style.height = `${BASE_HEIGHT}px`;
                    textarea.style.overflowY = "hidden";
                  }
                }}
                placeholder={isConversationEnded ? "" : t("chatBox.inputPlaceholder")}
                className={`w-full px-3.5 py-2.5 rounded-xl resize-none leading-6
                                            text-[14px] transition-all duration-200
                                            placeholder:text-slate-400 dark:placeholder:text-slate-500
                                            focus:outline-none focus:ring-2
                                            ${isConversationEnded
                    ? "bg-slate-100 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-white/5 cursor-not-allowed text-center"
                    : `bg-white/80 dark:bg-slate-800/60
                                                   border border-slate-200 dark:border-white/10
                                                   text-slate-800 dark:text-slate-100
                                                   hover:border-teal-300 dark:hover:border-teal-700
                                                   focus:border-teal-400 dark:focus:border-teal-500
                                                   focus:ring-teal-400/30 dark:focus:ring-teal-500/20`
                  }`}
                rows={1}
              />
            </div>
            {/* Send button */}
            <button
              onClick={handleSendMessage}
              disabled={!userMessage.trim() || isConversationEnded}
              title={t("chatBox.send")}
              className="group relative flex-shrink-0 h-10 w-10 rounded-xl
                                       flex items-center justify-center
                                       bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800
                                       hover:from-teal-500 hover:via-teal-600 hover:to-teal-700
                                       text-white
                                       shadow-md shadow-teal-700/30
                                       hover:shadow-lg hover:shadow-teal-600/40
                                       active:scale-95
                                       disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-md
                                       transition-all duration-300
                                       overflow-hidden">
              {/* sheen */}
              <span className="absolute inset-0 -translate-x-full
                                             group-hover:translate-x-full
                                             transition-transform duration-700
                                             bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              <Send size={17} className="relative z-10 -scale-x-100" />
            </button>

          </div>

        </div>
      )}

      <style>{`
                @keyframes shimmer {
                    0%   { background-position: 0% 50%; }
                    100% { background-position: 200% 50%; }
                }
            `}</style>
    </div>
  );
};

export default ChatBox;